import React, { useEffect, useState } from 'react';
import { getAuditLog } from '../../services/adminService';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface AuditLogItem {
  id: string;
  adminEmail: string;
  actionType: string;
  targetUserEmail: string;
  targetResource: string | null;
  details: any;
  createdAt: string;
}

const ACTION_BADGE_STYLES: Record<string, string> = {
  delete_user: 'bg-red-100 text-red-800',
  suspend_user: 'bg-red-100 text-red-800',
  cancel_subscription: 'bg-red-100 text-red-800',
  create_user: 'bg-green-100 text-green-800',
  activate_user: 'bg-green-100 text-green-800',
  update_user: 'bg-blue-100 text-blue-800',
  update_subscription: 'bg-blue-100 text-blue-800',
  extend_subscription: 'bg-blue-100 text-blue-800',
  add_bonus_generations: 'bg-purple-100 text-purple-800',
  set_license: 'bg-purple-100 text-purple-800',
  credit_user: 'bg-purple-100 text-purple-800',
};

const ACTION_TYPES = [
  'create_user',
  'update_user',
  'delete_user',
  'suspend_user',
  'activate_user',
  'update_subscription',
  'extend_subscription',
  'cancel_subscription',
  'add_bonus_generations',
  'set_license',
  'credit_user',
];

const formatActionName = (action: string) =>
  action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const AuditLog: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, actionTypeFilter]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: { page: number; limit: number; actionType?: string } = { page, limit };
      if (actionTypeFilter) params.actionType = actionTypeFilter;
      const data = await getAuditLog(params);
      setItems(data.auditLog || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load audit log', err);
      setError('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const renderDetails = (details: any) => {
    if (!details || (typeof details === 'object' && Object.keys(details).length === 0)) {
      return <p className="text-sm text-gray-500 italic">No details</p>;
    }
    const entries = typeof details === 'object' ? Object.entries(details) : [['value', details]];
    return (
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {entries.map(([key, val]) => (
          <React.Fragment key={key}>
            <span className="text-xs font-medium text-gray-500">{key}</span>
            <span className="text-xs text-gray-800">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-sm text-gray-600">Track admin actions and changes</p>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <select
          value={actionTypeFilter}
          onChange={(e) => { setActionTypeFilter(e.target.value); setPage(1); }}
          className="block w-full sm:w-64 pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
        >
          <option value="">All action types</option>
          {ACTION_TYPES.map(t => (
            <option key={t} value={t}>{formatActionName(t)}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : (
        <div className="bg-white rounded shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map(item => {
                  const isExpanded = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {new Date(item.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{item.adminEmail}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_BADGE_STYLES[item.actionType] || 'bg-gray-100 text-gray-800'}`}>
                            {formatActionName(item.actionType)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{item.targetUserEmail}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : item.id)}
                            className="text-gray-400 hover:text-gray-600"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 bg-gray-50 border-b">
                            {renderDetails(item.details)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">No audit log entries found</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-600">Total: {total}</div>
              <div className="space-x-2">
                <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Prev</button>
                <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
